/**
 * @file    DynamicTable.tsx
 * @scope   SHARED
 * @summary Orchestrator of the DynamicTable widget: composes the domain hooks and the views.
 *          Owns the table state; owns no effect (every effect lives in a named hook).
 * @consumes common/hooks, common/utils, Views
 * @produces The DynamicTable widget.
 * @sideEffects None directly — see the hooks it composes.
 */
import './common/styles/DynamicTable.scss';
import React, { useRef, useState } from 'react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { resolveSortingType, SortingTypeValue } from 'helpers/DataGridSorting';
import { WidgetAction } from 'models/WidgetModel';
import { exportHtmlFileDynamicTable, exportToCSV, generateExcelFile } from 'utils/exportingFiles';
import { exportToImage } from 'utils/exportingToImage';
import { DynamicTableProps, TableState } from './common/types';
import { prepareEnterpriseData } from './common/utils/DynamicTableUtils';
import { buildLineChartOptions, getColumnFieldKey } from './common/utils/lineChartOptionsBuilder';
import { getLineChartData } from './common/utils/lineChartData';
import { hasSubtitle, joinClasses } from './common/utils/tableLayout';
import { useEyeIcon } from './common/hooks/useEyeIcon';
import { useFetchTriggers } from './common/hooks/useFetchTriggers';
import { useLineChartSettings } from './common/hooks/useLineChartSettings';
import { useTableFetch } from './common/hooks/useTableFetch';
import { useTableLayout } from './common/hooks/useTableLayout';
import { useTableResponseSync } from './common/hooks/useTableResponseSync';
import { useWidgetActions, WidgetActionHandlers } from './common/hooks/useWidgetActions';
import { buildColumns } from './Views/TableGrid/columnsBuilder';
import TableBody from './Views/TableBody/TableBody';
import TableHeaderInfo from './Views/TableHeaderInfo/TableHeaderInfo';
import TableFootNote from './Views/TableFootNote/TableFootNote';

const DynamicTable: React.FC<DynamicTableProps> = ({
  response: defaultResponse,
  payload,
  options,
  customWidgetConfig,
  ...props
}) => {
  const { loading } = props;
  const { chartView, selectedValueL1, showLineChart: contextShowLineChart } = useWidgetContext();
  const { pagination, fixedHeader, showTotals, sortServer } = options?.table || {};
  const { displayTimePeriodDelta = false, decimalPlaces } = options || {};

  const [table, setTable] = useState<TableState>();
  const echartRef = useRef<any>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);
  const totalsFirstRow = useRef<any>();
  totalsFirstRow.current = table?.totalHeader;
  const eyeIconBase64 = useEyeIcon();

  const { response, getData, controllerNameValue } = useTableFetch({
    payload,
    paginated: !!(pagination || sortServer),
    defaultPerPage: table?.paginationPerPage || 5,
    firstRender,
  });

  const setTableData = (res: any) => {
    if (!res?.data?.columns) return;
    const data = res.data.data;
    const { tableType, isSortable, requiresEnterprisePreparation } = resolveSortingType(payload);
    res.type = tableType;
    if (requiresEnterprisePreparation) prepareEnterpriseData(data);

    const columns = buildColumns(res.data, res.type as SortingTypeValue, {
      payload,
      defaultResponse,
      decimalPlaces,
      displayTimePeriodDelta,
      selectedValueL1,
      showTotals,
      totalsFirstRow,
      // QUIRK: bitwise OR kept from the original implementation.
      idSurveyPanel: response?.data?.idSurveyPanel | defaultResponse?.data?.idSurveyPanel,
    });

    setTable({
      rawData: res.data,
      columns,
      data,
      totalHeader: showTotals && data instanceof Array && data.length ? data[0] : null,
      paginationTotalRows: res.data.total,
      paginationPerPage: res.data.per_page,
      stats: res.data.summaryStats,
      type: tableType,
      enableSorting: isSortable,
    });
  };

  const { chartData } = useTableResponseSync({
    defaultResponse,
    response,
    decimalPlaces,
    displayTimePeriodDelta,
    firstRender,
    onTableData: setTableData,
  });

  const { showLineChart, setShowLineChart, yAxisValues, goalValues, drawGoalLine } =
    useLineChartSettings(options);

  const displayName = props.displayName || '';
  const followContextLineChart = () => setShowLineChart(contextShowLineChart ?? false);
  const withLineChartSync = (run: () => void) => () => {
    followContextLineChart();
    run();
  };

  const actionHandlers: WidgetActionHandlers = {
    [WidgetAction.SWITCH_CHART_TABLE]: () => setShowLineChart(false),
    [WidgetAction.SWITCH_LINECHART_TABLE]: followContextLineChart,
    [WidgetAction.SWITCH_BAR_LINE]: () => undefined,
    [WidgetAction.SEARCH_FILTER]: withLineChartSync(() => {
      firstRender.current = false;
      getData(1);
    }),
    [WidgetAction.EXPORT_TO_CSV]: withLineChartSync(() =>
      exportToCSV(table, displayName, showTotals, customWidgetConfig)
    ),
    [WidgetAction.EXPORT_TO_XLSX]: withLineChartSync(() =>
      generateExcelFile(table, displayName, showTotals, customWidgetConfig)
    ),
    [WidgetAction.EXPORT_TO_HTML]: withLineChartSync(() =>
      exportHtmlFileDynamicTable(table, displayName, customWidgetConfig)
    ),
    [WidgetAction.EXPORT_TO_PNG]: withLineChartSync(() =>
      exportToImage(props.displayName, false, props?.widgetId, showLineChart)
    ),
    [WidgetAction.EXPORT_TO_JPEG]: withLineChartSync(() =>
      exportToImage(props.displayName, true, props?.widgetId, showLineChart)
    ),
  };
  useWidgetActions(actionHandlers);

  const { lineChartHeight, showScroll } = useTableLayout(
    tableRef,
    echartRef,
    showLineChart,
    table?.data
  );
  useFetchTriggers(getData, firstRender);

  const lineChartOptions = showLineChart
    ? buildLineChartOptions({
        chartData: getLineChartData(table),
        rows: table?.data ?? [],
        firstColumnField: table?.columns?.[0] ? getColumnFieldKey(table.columns[0]) : undefined,
        decimalPlaces,
        goalValues,
        drawGoalLine,
        yAxisValues,
        eyeIconBase64,
      })
    : undefined;

  const bodyClassName = joinClasses(
    'pck-table',
    !showLineChart && showScroll && 'sc-container',
    chartView ? 'dt-max-height' : !showLineChart && fixedHeader && 'fixed-header'
  );

  return (
    <div
      className={joinClasses(
        'pck-table-container',
        hasSubtitle(table?.rawData, customWidgetConfig) && 'separator-line'
      )}
    >
      <TableHeaderInfo
        rawData={table?.rawData}
        stats={table?.stats}
        customWidgetConfig={customWidgetConfig}
        controllerNameValue={controllerNameValue}
        chartView={chartView}
        showTotals={showTotals}
      />
      <div className={bodyClassName} ref={tableRef}>
        <TableBody
          showLineChart={showLineChart}
          chartView={chartView}
          echartRef={echartRef}
          lineChartOptions={lineChartOptions}
          lineChartHeight={lineChartHeight}
          chartData={chartData}
          options={options}
          widgetProps={props}
          table={table}
          loading={loading}
        />
      </div>
      <TableFootNote footNote={table?.rawData?.footNote} />
    </div>
  );
};

export default DynamicTable;
